import { contextStore } from '@wix/wix-chatbot-common/browser';

import { WIDGET_CREATE_CONTEXT_URL, WIDGET_INIT_URL } from '../constants';
import { WidgetDataRequestFailure } from '../errors';
import { fedopsLogger } from '../fedops';
import { dispatch } from '../store';
import type { InitWidgetResponse } from '../types';
import { assertExists, httpClient, isMobile } from '../utils';
import { widgetModel } from '../widgetModel';
import type { DataFetcher } from './types';

export class InitWidgetService implements DataFetcher<InitWidgetResponse> {
  private initialWidgetData: InitWidgetResponse;

  async get(): Promise<InitWidgetResponse | undefined> {
    if (this.initialWidgetData === undefined) {
      await this.fetch();
    }
    assertExists(this.initialWidgetData, 'InitialWidgetData should be defined after load');
    return this.initialWidgetData;
  }

  async fetch(): Promise<InitWidgetResponse | undefined> {
    fedopsLogger.interactionStarted('fetching-initial-widget-data');
    const { data } = await httpClient.getInstance().post(WIDGET_INIT_URL, {
      brand: widgetModel.getBrand(),
      parentUrl: window.location.href.split('?')[0],
    });
    fedopsLogger.interactionEnded('fetching-initial-widget-data');
    if (data.error) {
      throw new WidgetDataRequestFailure(data.error.message, 'getWidgetData');
    }
    dispatch('app/setInitialData', data);

    this.initialWidgetData = data;

    // Create context asynchronously after successful initialization
    this.createContext(data);

    return this.initialWidgetData;
  }

  private async createContext(initialWidgetData: InitWidgetResponse): Promise<void> {
    const { userContext } = initialWidgetData;

    if (!userContext?.userId) {
      return;
    }

    contextStore.setUserData(
      userContext.userId,
      userContext.userExperience,
      userContext.careProfile,
      userContext.accountId,
    );
    contextStore.setPlatform(isMobile());
    contextStore.setIsWidget(true);

    const experiments = widgetModel.getExperiments();
    if (experiments.enabled('specs.chatbot.disableContextCreation')) {
      return;
    }

    const context = contextStore.getContextForApi();

    try {
      // Merge userContext data with contextData to create complete context
      const completeContextData = {
        ...context,
        userId: userContext.userId,
        accountId: userContext.accountId,
        userExperience: userContext.userExperience,
        careProfile: userContext.careProfile,
      };
      fedopsLogger.interactionStarted('creating-context');
      await httpClient.getInstance().post(WIDGET_CREATE_CONTEXT_URL, {
        context: completeContextData,
      });
    } catch (error) {
      // Context creation failure should not break the widget
      console.error('Context creation failed:', error);
    }
    fedopsLogger.interactionEnded('creating-context');
  }
}
